/**
 * Renewal calendar: one month, one cell per ET day.
 *
 * Days ahead show renewals SCHEDULED (projected from each active
 * subscription's next billing date and cycle); days behind show what was
 * CHARGED — renewals paid, and subscriptions whose renewal failed (counted
 * once however often dunning retried, attempts beside it). Today shows both.
 * Below it, dunning efficacy for the same month (DunningEfficacy). Click a day
 * for every renewal on it, row by row, each with an Odin link (DayDetailSheet,
 * /manifold/hs-subscriptions/calendar/day). Data from /manifold/hs-subscriptions/calendar.
 */

import { useState, useEffect, useMemo } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react'

import { manifoldAPI } from '../../services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui'
import { odinPatientUrl } from '../../lib/portalAuthEvents'
import DunningEfficacy from './DunningEfficacy'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "3 failed (5 attempts)" — the attempts only when retries add some. */
const failedLabel = (failed, attempts) => `${failed.toLocaleString()} failed${attempts > failed ? ` (${attempts.toLocaleString()} attempts)` : ''}`

const money = cents => (cents ? `$${Math.round(cents / 100).toLocaleString()}` : '$0')

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month) {
  const [y, m] = month.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function thisMonthET() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date()).slice(0, 7)
}

export default function RenewalCalendar() {
  const [month, setMonth] = useState(thisMonthET)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setSelected(null)
    manifoldAPI.getRenewalCalendar({ month })
      .then(response => { if (alive && response.data.success) setData(response.data.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [month])

  // Leading blanks so the 1st lands on its weekday.
  const cells = useMemo(() => {
    if (!data || data.month !== month) return []
    const lead = new Date(`${month}-01T12:00:00Z`).getUTCDay()
    return [...Array(lead).fill(null), ...data.days]
  }, [data, month])

  const busiest = useMemo(() => Math.max(1, ...(data?.days || []).map(d => d.scheduled + d.paid + d.failed)), [data])
  const selectedDay = data?.days.find(d => d.date === selected)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Renewal calendar</CardTitle>
            <CardDescription className="text-xs">
              Ahead: renewals scheduled, projected from each active subscription&apos;s next billing date and cycle.
              Behind: renewals paid, and subscriptions whose renewal failed, each counted once however many times it was retried. Eastern time.
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setMonth(m => shiftMonth(m, -1))} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="w-36 text-center text-sm font-medium">{monthLabel(month)}</span>
            <Button size="sm" variant="outline" onClick={() => setMonth(m => shiftMonth(m, 1))} aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
            {month !== thisMonthET() && (
              <Button size="sm" variant="ghost" onClick={() => setMonth(thisMonthET())}>Today</Button>
            )}
          </div>
        </div>
        {data && data.month === month && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-xs text-muted-foreground tabular-nums">
            <span><span className="font-medium text-foreground">{data.totals.scheduled.toLocaleString()}</span> scheduled · {money(data.totals.scheduledAmount)}</span>
            <span><span className="font-medium text-foreground">{data.totals.paid.toLocaleString()}</span> renewed · {money(data.totals.paidAmount)}</span>
            <span title="Subscriptions whose renewal failed at least once this month, each counted once; attempts include every dunning retry">
              <span className="font-medium text-foreground">{data.totals.failed.toLocaleString()}</span> failed
              {data.totals.failedAttempts > data.totals.failed && ` (${data.totals.failedAttempts.toLocaleString()} attempts)`}
            </span>
            {(data.totals.walletPaid > 0 || data.totals.walletFailed > 0) && (
              <span title="Renewals charged to an Apple Pay or Google Pay wallet, included in the counts beside it">
                Apple/Google Pay: <span className="font-medium text-foreground">{data.totals.walletPaid}</span> renewed
                {data.totals.walletFailed > 0 && <span className="text-destructive"> · {data.totals.walletFailed} failed</span>}
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && !cells.length && (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}
        {cells.length > 0 && (
          <div className={loading ? 'opacity-60' : ''}>
            <div className="grid grid-cols-7 gap-1 text-[11px] text-muted-foreground mb-1">
              {WEEKDAYS.map(d => <div key={d} className="px-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, i) => {
                if (!day) return <div key={`blank-${i}`} />
                const past = day.date < data.today
                const isToday = day.date === data.today
                const volume = day.scheduled + day.paid + day.failed
                const shade = volume ? 0.08 + 0.32 * (volume / busiest) : 0
                return (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => setSelected(day.date === selected ? null : day.date)}
                    className={`min-h-[4.5rem] rounded-md border p-1.5 text-left text-[11px] leading-tight tabular-nums transition-colors
                      ${isToday ? 'border-primary' : ''} ${selected === day.date ? 'ring-2 ring-primary' : ''} ${past ? 'text-muted-foreground' : ''}`}
                    style={{ backgroundColor: shade ? `hsl(var(--primary) / ${shade})` : undefined }}
                  >
                    <div className="font-medium text-foreground">{Number(day.date.slice(8))}</div>
                    {day.scheduled > 0 && (
                      <div className="mt-0.5">{day.scheduled} due<span className="hidden sm:inline"> · {money(day.scheduledAmount)}</span></div>
                    )}
                    {(day.paid > 0 || day.failed > 0) && (
                      <div className="mt-0.5">
                        <div className="text-foreground">{day.paid + day.failed} processed</div>
                        {day.paid} paid{day.failed > 0 && <span className="text-destructive" title={`${day.failedAttempts} attempts`}> · {day.failed} failed</span>}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <DunningEfficacy month={month} />
        <DayDetailSheet day={selectedDay} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  )
}

const KIND = {
  scheduled: { label: 'Due', className: 'text-foreground' },
  paid: { label: 'Paid', className: 'text-green-600' },
  failed: { label: 'Failed', className: 'text-destructive' },
}

const timeET = ms => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })

/** One day opened up: its totals, then every renewal row with an Odin link. */
function DayDetailSheet({ day, onClose }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [kind, setKind] = useState('all')

  useEffect(() => {
    if (!day) return undefined
    let alive = true
    setRows(null)
    setError(null)
    setKind('all')
    manifoldAPI.getRenewalDay({ date: day.date })
      .then(response => { if (alive && response.data.success) setRows(response.data.data.rows) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
    return () => { alive = false }
  }, [day?.date])

  const counts = (rows || []).reduce((acc, row) => ({ ...acc, [row.kind]: (acc[row.kind] || 0) + 1 }), {})
  const shown = (rows || []).filter(row => kind === 'all' || row.kind === kind)

  return (
    <Sheet open={Boolean(day)} onOpenChange={open => { if (!open) onClose() }}>
      <SheetContent className="overflow-y-auto">
        {day && <>
          <SheetHeader>
            <SheetTitle>{new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</SheetTitle>
            <SheetDescription asChild>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs tabular-nums">
                {day.scheduled > 0 && <span>{day.scheduled} scheduled · {money(day.scheduledAmount)}</span>}
                {day.paid > 0 && <span>{day.paid} renewed · {money(day.paidAmount)}</span>}
                {day.failed > 0 && <span className="text-destructive">{failedLabel(day.failed, day.failedAttempts)}</span>}
                {(day.walletPaid > 0 || day.walletFailed > 0) && (
                  <span>Apple/Google Pay: {day.walletPaid} renewed{day.walletFailed > 0 && <span className="text-destructive"> · {day.walletFailed} failed</span>}</span>
                )}
                {!day.scheduled && !day.paid && !day.failed && <span>Nothing scheduled or charged.</span>}
              </div>
            </SheetDescription>
          </SheetHeader>

          {rows && rows.length > 0 && (
            <div className="flex flex-wrap gap-1" role="group" aria-label="Filter renewals">
              {[['all', `All ${rows.length}`], ...Object.keys(KIND).filter(k => counts[k]).map(k => [k, `${KIND[k].label} ${counts[k]}`])].map(([key, label]) => (
                <Button key={key} size="sm" variant={kind === key ? 'default' : 'outline'} onClick={() => setKind(key)}>{label}</Button>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
          {!rows && !error && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No renewals on this day.</p>}
          {shown.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs tabular-nums">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left font-medium">Time</th>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-left font-medium">Customer</th>
                    <th className="p-2 text-left font-medium">Plan</th>
                    <th className="p-2 text-right font-medium">Amount</th>
                    <th className="p-2 text-right font-medium"><span className="sr-only">Links</span></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(row => {
                    const odin = odinPatientUrl(row.patient_id)
                    return (
                      <tr key={`${row.kind}-${row.payment_id || row.subscription_id}`} className="border-t align-top">
                        <td className="p-2 whitespace-nowrap">{row.kind === 'scheduled' ? '—' : timeET(row.at)}</td>
                        <td className={`p-2 font-medium ${KIND[row.kind].className}`}>
                          {KIND[row.kind].label}
                          {row.wallet && <div className="font-normal text-muted-foreground">{row.wallet}</div>}
                          {row.failure && <div className="max-w-[14rem] font-normal text-destructive/80" title={row.failure_detail || row.failure}>{row.failure}</div>}
                        </td>
                        <td className="p-2">
                          <div className="font-medium text-foreground">{row.customer_name || '—'}</div>
                          <div className="text-muted-foreground">{row.customer_email || 'No customer match'}</div>
                        </td>
                        <td className="p-2 font-mono">
                          {row.plan || '—'}
                          {row.billing_period && <div className="font-sans text-muted-foreground">every {row.billing_period} {row.billing_period_unit}{row.billing_period === 1 ? '' : 's'}</div>}
                        </td>
                        <td className="p-2 text-right">{money(row.amount)}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {odin
                            ? <a href={odin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Open in Odin <ExternalLink className="h-3 w-3" /></a>
                            : <span className="text-muted-foreground" title="No core patient with this email">No Odin match</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>}
      </SheetContent>
    </Sheet>
  )
}
